// ═══════════════════════════════════════════════════════════════════════════════
// L3 upgrade — restore archived high-tier tasks modal (§22.7)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  TASK_KIND,
  type RestorableArchivedTaskItem,
} from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';
import { RecurrenceBadge } from '../task/RecurrenceBadge';

type RestoreArchivedTasksModalProps = {
  open: boolean;
  items: RestorableArchivedTaskItem[];
  busy: boolean;
  onConfirm: (taskIds: string[]) => void;
  onSkip: () => void;
};

const resolveKindLabel = (
  item: RestorableArchivedTaskItem,
  t: (key: string) => string,
): string => {
  if (item.taskKind === TASK_KIND.INVENTORY) {
    return t('task.list.kindStockTake');
  }
  if (item.taskKind === TASK_KIND.PICKUP_DROPOFF) {
    return t('task.list.kindPickupDropoff');
  }
  return t('task.list.kindMedication');
};

export const RestoreArchivedTasksModal = ({
  open,
  items,
  busy,
  onConfirm,
  onSkip,
}: RestoreArchivedTasksModalProps) => {
  const { t } = useLanguage();
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedKeys(new Set(items.map((item) => item.itemKey)));
  }, [open, items]);

  const selectedTaskIds = useMemo(() => {
    const ids: string[] = [];
    items.forEach((item) => {
      if (selectedKeys.has(item.itemKey)) {
        ids.push(...item.taskIds);
      }
    });
    return ids;
  }, [items, selectedKeys]);

  const handleToggleItem = (itemKey: string): void => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  const handleConfirm = (): void => {
    if (selectedTaskIds.length === 0) {
      return;
    }
    onConfirm(selectedTaskIds);
  };

  return (
    <AppModal
      open={open}
      onClose={onSkip}
      title={t('membership.restoreModal.title')}
      titleId="restore-archived-tasks-title"
      closeDisabled={busy}
      hideCloseButton
      closeAriaLabel={t('common.cancel')}
      backdropZClass="z-[70]"
      panelMaxWidthClass="max-w-lg"
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {t('membership.restoreModal.description')}
      </p>

      <ul className="mt-4 max-h-[50vh] space-y-2 overflow-y-auto" role="list">
        {items.map((item) => {
          const checked = selectedKeys.has(item.itemKey);
          const inputId = `restore-item-${item.itemKey}`;
          return (
            <li key={item.itemKey}>
              <label
                htmlFor={inputId}
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60"
              >
                <input
                  id={inputId}
                  type="checkbox"
                  checked={checked}
                  disabled={busy}
                  onChange={() => handleToggleItem(item.itemKey)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{item.displayTitle}</span>
                    <span className="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-950/50 dark:text-sky-200">
                      {resolveKindLabel(item, t)}
                    </span>
                    <RecurrenceBadge
                      recurrence={item.recurrence}
                      recurrenceDays={item.recurrenceDays}
                      size="sm"
                    />
                  </span>
                  {item.displaySubtitle.trim() !== '' ? (
                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                      {item.displaySubtitle}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <AppButton type="button" variant="secondary" onClick={onSkip} disabled={busy}>
          {t('membership.restoreModal.skip')}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          onClick={handleConfirm}
          disabled={busy || selectedTaskIds.length === 0}
        >
          {busy ? t('common.loading') : t('membership.restoreModal.confirm')}
        </AppButton>
      </div>
    </AppModal>
  );
};
